import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@rook/shared/auth/AuthProvider";
import { Button } from "@rook/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@rook/shared/components/ui/card";
import { Checkbox } from "@rook/shared/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@rook/shared/components/ui/form";
import { Input } from "@rook/shared/components/ui/input";
import { usePageTitle } from "@rook/shared/lib/usePageTitle";

import { login } from "./api";
import { loginSchema, type LoginInput } from "./schemas";

export const LoginView = () => {
  usePageTitle("Sign in");
  const navigate = useNavigate();
  const auth = useAuth();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", remember: true },
  });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: async () => {
      // Re-hydrate the session query so AuthGuard immediately sees
      // the new state without waiting for the focus-refetch tick.
      await auth.refresh();
      navigate("/home", { replace: true });
    },
  });

  const errorMessage =
    mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <Card>
      <CardHeader>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          Sign in
        </p>
        <CardTitle className="text-2xl">Welcome back</CardTitle>
        <CardDescription>
          Sign in to manage your ROOK integration, webhooks and data feeds.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {errorMessage && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="name@mail.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="Your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-between">
              <FormField
                control={form.control}
                name="remember"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal">
                      Remember me
                    </FormLabel>
                  </FormItem>
                )}
              />
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-rook-light hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              size="lg"
              disabled={mutation.isPending}
              className="mt-2"
            >
              {mutation.isPending ? "Signing in…" : "Log in"}
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link
            to="/create-account"
            className="font-semibold text-rook-light hover:underline"
          >
            Create one
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
